/**
 * 「页面上有模态对话框开着」的判据（F3 §6 第 6 点 / §9.1 ④ 实测）。
 *
 * Radix 的模态对话框（Dialog / AlertDialog / Sheet）打开时会锁 body 的 pointer-events、
 * 锁滚动，并把焦点困在对话框里——浮窗在这期间打不了字（字会进对话框）。所以浮窗此时只读。
 *
 * 判据三条同时成立：
 * 1. body `pointer-events: none`（模态层在）——自写的 `Flyout` 也是 role=dialog，但不锁 body；
 * 2. 有 `role=dialog|alertdialog` 且 `data-state=open` 的元素——只开着下拉 / Select 时不算：
 *    它们没有焦点陷阱，点浮窗会自然把它们关掉；
 * 3. 那个对话框不是浮窗自己开的：不在浮窗元素里，也不在浮窗的内层（`ASSISTANT_INNER_LAYER`，
 *    浮窗里 portal 出去的弹层都经 StackingLayerProvider 带上这个 z class）。
 */

/** 浮窗本体的层（§9.1 ③） */
export const ASSISTANT_WINDOW_LAYER = 'z-[90]'
/** 浮窗里打开的弹层（Select / Popover / Sheet / 关闭确认）的层；同时是「浮窗自己的弹层」的标记 */
export const ASSISTANT_INNER_LAYER = 'z-[95]'
/**
 * 顶栏里弹出的菜单 / 面板（头像菜单、通知、提醒、收藏、导出、语言）的层：用户主动点开的临时面板，
 * 要盖在浮窗（默认就贴在顶栏下方右侧）之上（live 09-14：否则浮窗开着点不到 Log out）
 */
export const ASSISTANT_TOPBAR_LAYER = 'z-[96]'
/**
 * 页面上开着用户自己打开的创建面板（CreateFlyout，z-50 起）时，浮动的浮窗和最小化小条沉到这一层：在面板下面、
 * 在页面内容和右下角悬浮球（z-40）上面（2026-10-29）。停靠的浮窗不沉：面板本来就让出它的宽度，不重叠。
 */
export const ASSISTANT_UNDER_PANEL_LAYER = 'z-[45]'

export function isPageModalOpen(windowEl: HTMLElement | null): boolean {
  if (!windowEl) return false
  const doc = windowEl.ownerDocument
  if (doc.body.style.pointerEvents !== 'none') return false
  const dialogs = doc.querySelectorAll<HTMLElement>('[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]')
  return Array.from(dialogs).some(el => !windowEl.contains(el) && !el.classList.contains(ASSISTANT_INNER_LAYER))
}
